import api, { ENDPOINTS } from '@tyrio/api-factory';
import { PosApi } from '@tyrio/dto';
import { ToastHelper, ToastMessageType, ToastType } from '@tyrio/ui-library';
import { AxiosError } from 'axios';
import _ from 'lodash';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../../../context/AuthContext';
import { POSContext } from '../../../context/POSContext';
import { usePosSearchContext } from '../components/SearchPOS/SearchContext';

export const useGetPosData = (filterValues: qs.ParsedQs) => {
  const { user } = useAuth();
  const { categoryId, motoDesiredOption, selectedSizes } =
    usePosSearchContext();

  const {
    setMyStockItems,
    setBranchesStockItems,
    setSupplierStockItems,
    selectedWarehouseId,
  } = useContext(POSContext);

  const myStockItemsQueryKey = 'get_my_stock_pos_list';
  const branchesStockItemsQueryKey = 'get_branches_stock_pos_list';
  const supplierStockItemsQueryKey = 'get_supplier_stock_pos_list';

  const formSets =
    filterValues?.['size']?.length === 2 &&
    (filterValues?.['categoryId'] === '1' ||
      filterValues?.['categoryId'] === '2');

  const isQueryEnabled =
    categoryId === 2
      ? !(selectedSizes?.length === 1 && motoDesiredOption === 'FRONT/REAR')
      : true;

  const queryFn = (key: keyof typeof ENDPOINTS) => {
    return api.fetch<PosApi['list']>(key, {
      query: {
        ...filterValues,
        formSets: _.toString(formSets),
      },
      warehouseId:
        selectedWarehouseId?.toString() ?? user?.currentBranchId?.toString(),
    });
  };

  const {
    status: myStockItemsStatus,
    isRefetching: myStockRefetching,
    isFetching: myStockFetching,
  } = useQuery(
    [myStockItemsQueryKey, filterValues],
    () => queryFn(myStockItemsQueryKey),
    {
      enabled: isQueryEnabled,
      onSuccess: (res) => {
        setMyStockItems({ ...res });
      },
    }
  );

  const {
    status: branchesStockItemsStatus,
    isRefetching: branchesRefetching,
    isFetching: branchesFetching,
  } = useQuery(
    [branchesStockItemsQueryKey, filterValues],
    () => queryFn(branchesStockItemsQueryKey),
    {
      enabled: isQueryEnabled,
      onSuccess: (res) => {
        setBranchesStockItems({ ...res });
      },
    }
  );

  const {
    status: supplierStockItemsStatus,
    isRefetching: suppliersRefetching,
    isFetching: suppliersFetching,
  } = useQuery(
    [supplierStockItemsQueryKey, filterValues],
    () => queryFn(supplierStockItemsQueryKey),
    {
      enabled: isQueryEnabled,
      onSuccess: (res) => {
        setSupplierStockItems({ ...res });
      },
      onError: (err: unknown) => {
        if (err instanceof AxiosError)
          ToastHelper.showToast(
            'Supplier stock',
            ToastType.ERROR,
            ToastMessageType.ERROR,
            err?.response?.data?.error?.name ?? ''
          );
      },
    }
  );

  const showMyStockLoader =
    myStockItemsStatus === 'loading' || myStockFetching || myStockRefetching;

  const showBranchesLoader =
    branchesStockItemsStatus === 'loading' ||
    branchesRefetching ||
    branchesFetching;

  const showSuppliersLoader =
    supplierStockItemsStatus === 'loading' ||
    suppliersRefetching ||
    suppliersFetching;

  return {
    showMyStockLoader,
    showBranchesLoader,
    showSuppliersLoader,
  };
};
